import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataClass } from 'scrivito'

import { FieldLabel } from '@/Atomic/Atoms/FieldLabel'
import { Radio } from '@/Atomic/Atoms/Radio'
import { Button } from '@/Atomic/Molecules/Button'
import InfoBox from '@/Atomic/Molecules/InfoBox'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import { DataClassName } from '@/Data/const'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'

import './DataClass.ListDrawer.scss'

type ListType = 'static' | 'dynamic'

interface DataClassListDrawerProps {
  dataClass: DataClass
  contactEntity: DataClassName.COMPANY | DataClassName.PERSON
  isOpen: boolean
  close: () => void
}

export const DataClassListDrawer: React.FC<DataClassListDrawerProps> = ({
  dataClass,
  contactEntity,
  isOpen,
  close,
}) => {
  const { t } = useTranslation()

  const { methods, onFormSubmit } = useDataClassFormHandler(dataClass, {
    prefill: { enabled: isOpen },
  })
  const { getValues, watch, reset, setValue } = methods
  const selectedListType = (watch('type') as ListType | undefined) ?? 'static'
  const formGroups = getDataClassFormMap(dataClass, getValues, watch)
  const typeField = formGroups
    .flatMap((group) => group.fields)
    .find((field) => field.name === 'type')

  const typeRequirementState =
    typeField?.fieldSchema?.mandatory === undefined
      ? undefined
      : typeField.fieldSchema.mandatory
        ? 'required'
        : 'optional'
  const getListTypeLabel = (type: ListType, fallbackKey: string) => {
    return (
      typeField?.enumValueOptions?.find((option) => option.value === type)
        ?.label ?? t(fallbackKey)
    )
  }

  const groups = formGroups
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) => field.name !== 'type'),
    }))
    .filter((group) => group.fields.length > 0)

  const updateListType = (type: ListType) => {
    setValue('type', type, { shouldDirty: true, shouldValidate: true })
  }

  const submitWithListType = (event?: Parameters<typeof onFormSubmit>[0]) => {
    setValue('type', selectedListType)
    setValue('entityType', contactEntity)
    onFormSubmit(event)
  }

  const onDrawerClose = () => {
    reset()
    close()
  }

  return (
    <Drawer isOpen={isOpen} close={onDrawerClose} direction="right">
      <DrawerHeader>
        <span className="fw-bold fs-5 lh-base" style={{ color: '#141414' }}>
          {contactEntity === DataClassName.COMPANY
            ? t('pages.list.createCompaniesList')
            : t('pages.list.createPersonsList')}
        </span>
        <Button
          variant="icon"
          icon="cross"
          tooltip={t('tooltip.close')}
          onClick={onDrawerClose}
        />
      </DrawerHeader>
      <DrawerBody>
        <FormProvider {...methods}>
          <Form onSubmit={submitWithListType}>
            <FormBody>
              <InfoBox
                type="warning"
                iconPosition="side"
                text={t('pages.list.typeWarning')}
              />
              <fieldset className="list-type-radio-group" id="type">
                <legend className="visually-hidden">
                  {t('pages.list.typeSelection.legend')}
                </legend>
                <FieldLabel
                  name="type"
                  label={typeField?.title}
                  requirementState={typeRequirementState}
                />
                <div className="radios">
                  <Radio
                    name="type"
                    value="static"
                    checked={selectedListType === 'static'}
                    onChange={() => updateListType('static')}
                    label={getListTypeLabel(
                      'static',
                      'pages.list.typeSelection.static.label',
                    )}
                    description={t(
                      'pages.list.typeSelection.static.description',
                    )}
                  />
                  <Radio
                    name="type"
                    value="dynamic"
                    checked={selectedListType === 'dynamic'}
                    onChange={() => updateListType('dynamic')}
                    label={getListTypeLabel(
                      'dynamic',
                      'pages.list.typeSelection.dynamic.label',
                    )}
                    description={t(
                      'pages.list.typeSelection.dynamic.description',
                    )}
                  />
                </div>
              </fieldset>
              {groups.map((group) => (
                <FormGroup key={group.name}>
                  <FormFields>
                    {group.fields.map((field) => (
                      <CustomField
                        key={field.fieldKey}
                        fieldMap={field}
                        isEditing={true}
                        className="form-field"
                      />
                    ))}
                  </FormFields>
                </FormGroup>
              ))}
            </FormBody>
          </Form>
        </FormProvider>
      </DrawerBody>
      <DrawerFooter align="right">
        <Button variant="outlined" onClick={onDrawerClose} size="lg">
          {t('common.cancel')}
        </Button>
        <Button icon="disk" onClick={() => submitWithListType()} size="lg">
          {t('common.save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
